/* eslint-disable @typescript-eslint/no-unused-vars */

/* eslint-disable react/no-children-prop */
import { AppstoreAddOutlined, FilterOutlined, SearchOutlined } from '@ant-design/icons';
import { Badge, Button, Input, message, PageHeader, Table, Tooltip } from 'antd';
import { SortDir, TaskSortType } from 'core/client';
import { Colors, TaskStateColorDark } from 'core/interfaces/variables.types';
import { getAuthStore } from 'core/modules/auth/auth.store';
import { getSettingsStore } from 'core/modules/settings/settings.store';
import { TaskState } from 'core/modules/tasks/task.interface';
import { getTaskStore, TaskLoader } from 'core/modules/tasks/task.store';
import { getTaskSearchesStore } from 'core/modules/tasks/task-search.store';
import { mapReducer } from 'core/utils/store';
import { useDebounce } from 'core/utils/time';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TaskFiltersForm } from 'src/components/Tasks/FiltersForm';
import { TaskListColumnsPopover } from 'src/components/Tasks/List/ColumnsPopover';
import { TasksListSavedSearchesBar } from 'src/components/Tasks/List/SavedSearchesBar';
import { TasksTable } from 'src/components/Tasks/Table';
import { TasksTableActions } from 'src/components/Tasks/TableActions/TableActions';
import { TaskDrawer } from 'src/components/Tasks/TaskDrawer';
import { setRouterQuery } from 'src/utils/router';
import styled, { css } from 'styled-components';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

function getColumnSortOrder(search, name) {
  var _search$sort;

  if (((_search$sort = search.sort) === null || _search$sort === void 0 ? void 0 : _search$sort.sortBy) === name) {
    var _search$sort2;

    return ((_search$sort2 = search.sort) === null || _search$sort2 === void 0 ? void 0 : _search$sort2.sortDir) === SortDir.Asc ? 'ascend' : 'descend';
  }
}

export default function TasksListPage() {
  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var router = useRouter();

  var _getAuthStore = getAuthStore(),
      canManageTasks = _getAuthStore.canManageTasks;

  var _getTaskStore = getTaskStore(),
      tasks = _getTaskStore.tasks,
      isLoadingKey = _getTaskStore.isLoadingKey,
      getTasks = _getTaskStore.getTasks,
      populatedTasksPage = _getTaskStore.populatedTasksPage,
      getTaskById = _getTaskStore.getTaskById;

  var _getSettingsStore = getSettingsStore(),
      taskColumns = _getSettingsStore.taskColumns;

  var _getTaskSearchesStore = getTaskSearchesStore(),
      localSearch = _getTaskSearchesStore.localSearch,
      numFilters = _getTaskSearchesStore.numFilters;

  var _useState = useState(''),
      search = _useState[0],
      setSearch = _useState[1];

  var _useReducer = useReducer(mapReducer, {}),
      selected = _useReducer[0],
      dispatchSelected = _useReducer[1];

  var _useState2 = useState(false),
      isCreating = _useState2[0],
      setCreating = _useState2[1];

  var _useState3 = useState(false),
      showFilters = _useState3[0],
      setShowFilters = _useState3[1];

  var _useState4 = useState({
    page: 0,
    pageSize: 15
  }),
      paginationParams = _useState4[0],
      setPaginationParams = _useState4[1];

  var isLoading = isLoadingKey(TaskLoader.Fetch);
  var selectedIds = Object.keys(selected);
  var editingId = selectedIds.length === 1 ? selectedIds[0] : undefined;
  var debouncedSearch = useDebounce(search, 1000);
  var selectedItems = useMemo(function () {
    return selectedIds.map(getTaskById).filter(Boolean);
  }, [selectedIds]);
  var dataSource = useMemo(function () {
    return populatedTasksPage.tasks.filter(Boolean);
  }, [tasks, populatedTasksPage]);
  useEffect(function () {
    var _localSearch$filters, _localSearch$sort$sor, _localSearch$sort, _localSearch$sort$sor2, _localSearch$sort2;

    getTasks(paginationParams.page, (_localSearch$filters = localSearch === null || localSearch === void 0 ? void 0 : localSearch.filters) !== null && _localSearch$filters !== void 0 ? _localSearch$filters : {}, paginationParams.pageSize, (_localSearch$sort$sor = localSearch === null || localSearch === void 0 ? void 0 : (_localSearch$sort = localSearch.sort) === null || _localSearch$sort === void 0 ? void 0 : _localSearch$sort.sortBy) !== null && _localSearch$sort$sor !== void 0 ? _localSearch$sort$sor : TaskSortType.CreatedAt, (_localSearch$sort$sor2 = localSearch === null || localSearch === void 0 ? void 0 : (_localSearch$sort2 = localSearch.sort) === null || _localSearch$sort2 === void 0 ? void 0 : _localSearch$sort2.sortDir) !== null && _localSearch$sort$sor2 !== void 0 ? _localSearch$sort$sor2 : SortDir.Desc)["catch"](function () {
      message.error(t('unknownError'));
    });
  }, [paginationParams, localSearch]);
  useEffect(function () {
    var _localSearch$filters2;

    setSearch((_localSearch$filters2 = localSearch.filters) === null || _localSearch$filters2 === void 0 ? void 0 : _localSearch$filters2.contains);
  }, [localSearch]);
  useEffect(function () {
    if (router.query.taskId) {
      setCreating(false);
      dispatchSelected({
        type: 'addOne',
        key: router.query.taskId,
        value: true
      });
    } else if (selectedIds.length === 1) {
      dispatchSelected({
        type: 'clear'
      });
    }
  }, [router.query.taskId]);
  return /*#__PURE__*/_jsxs(Section, {
    className: (isCreating || !!editingId) && 'drawer-open',
    children: [/*#__PURE__*/_jsx(PageHeader, {
      title: t('tasksList.title'),
      subTitle: populatedTasksPage.total,
      extra: [/*#__PURE__*/_jsx(SearchInput, {
        type: "search",
        placeholder: t('form.task.content.filter'),
        value: search,
        onInput: function onInput(ev) {
          return setSearch(ev.target.value);
        },
        prefix: /*#__PURE__*/_jsx(SearchOutlined, {})
      }, "search"), /*#__PURE__*/_jsx(TaskListColumnsPopover, {}, "columns"), /*#__PURE__*/_jsx(Tooltip, {
        title: t('tasksList.filtersSettings'),
        children: /*#__PURE__*/_jsx(Badge, {
          count: numFilters,
          style: {
            backgroundColor: Colors.blue[4]
          },
          offset: [-5, 5],
          children: /*#__PURE__*/_jsx(Button, {
            onClick: function onClick() {
              return setShowFilters(!showFilters);
            },
            type: showFilters ? 'primary' : 'default',
            children: /*#__PURE__*/_jsx(FilterOutlined, {})
          })
        })
      }, "filters"), canManageTasks && /*#__PURE__*/_jsx(Button, {
        type: "primary",
        icon: /*#__PURE__*/_jsx(AppstoreAddOutlined, {}),
        disabled: isLoading,
        onClick: function onClick() {
          setRouterQuery(router, 'taskId');
          setCreating(true);
          dispatchSelected({
            type: 'clear'
          });
        },
        children: t('tasksList.add')
      }, "add")]
    }), /*#__PURE__*/_jsx(TasksListSavedSearchesBar, {}), /*#__PURE__*/_jsx(TaskFiltersForm, {
      visible: showFilters,
      contains: debouncedSearch
    }), /*#__PURE__*/_jsx(TasksTable, {
      isLoading: isLoading,
      visibleColumns: taskColumns !== null && taskColumns !== void 0 ? taskColumns : {},
      tasks: dataSource,
      onRow: function onRow(row) {
        return {
          className: "status-".concat(row === null || row === void 0 ? void 0 : row.state),
          onClick: function onClick() {
            if (selected[row.id]) {
              dispatchSelected({
                type: 'removeOne',
                key: row.id
              });
              setCreating(false);

              if (selectedIds.length === 2) {
                var otherId = selectedIds.find(function (id) {
                  return id !== row.id;
                });
                setRouterQuery(router, 'taskId', otherId);
              }

              if (selectedIds.length === 1) {
                setRouterQuery(router, 'taskId');
              }
            } else {
              dispatchSelected({
                type: 'addOne',
                key: row.id,
                value: true,
                clear: selectedIds.length < 2
              });

              if (selectedIds.length < 2) {
                setRouterQuery(router, 'taskId', row.id);
              }
            }
          }
        };
      },
      rowSelection: {
        type: 'checkbox',
        selectedRowKeys: selectedIds,
        columnWidth: 32,
        onChange: function onChange(selectedKeys) {
          var values = selectedKeys.reduce(function (result, key) {
            result[key] = true;
            return result;
          }, {});
          dispatchSelected({
            type: 'addMany',
            items: values,
            clear: true
          });

          if (selectedKeys.length === 1) {
            setRouterQuery(router, 'taskId', selectedKeys[0]);
          } else if (selectedKeys.length !== 1) {
            setRouterQuery(router, 'taskId');
          }
        }
      },
      pagination: {
        position: ['bottomCenter'],
        showSizeChanger: true,
        pageSize: paginationParams.pageSize,
        total: populatedTasksPage.total
      },
      onChange: function onChange(pagination) {
        setPaginationParams({
          page: pagination.current - 1,
          pageSize: pagination.pageSize
        });
      },
      summary: function summary() {
        return /*#__PURE__*/_jsx(Table.Summary.Row, {
          className: "table-actions",
          children: /*#__PURE__*/_jsx(Table.Summary.Cell, {
            index: 1,
            colSpan: 5,
            children: /*#__PURE__*/_jsx(TasksTableActions, {
              selectedItems: selectedItems
            })
          }, "buttons")
        });
      }
    }), /*#__PURE__*/_jsx(TaskDrawer, {
      isVisible: isCreating,
      canToggleExpanded: false,
      onTaskCreated: function onTaskCreated(id) {
        setCreating(false);
        dispatchSelected({
          type: 'addOne',
          key: id,
          value: true,
          clear: true
        });
        setRouterQuery(router, 'taskId', id);
        setRouterQuery(router, 'expanded', 'true');
      },
      onClose: function onClose() {
        setCreating(false);
        dispatchSelected({
          type: 'clear'
        });
      }
    })]
  });
} // Style

var Section = styled.section.withConfig({
  displayName: "list__Section",
  componentId: "sc-kulv8x-0"
})(["margin-right:0px;@media (min-width:1350px){&.drawer-open{margin-right:550px;}}.ant-table-row .state{font-size:18px;position:absolute;margin:-12px 0;", "}.TaskDeadline{width:100%;}.content-report{em{margin-left:-5px;color:", ";}}.details-column{display:flex;flex-wrap:wrap;margin-bottom:-4px;> *{margin-bottom:4px;}}.ant-table-column-sorter-inner{display:none;}.ant-table-cell{padding:16px 9px;> span{text-align:center;}}.ant-table-column-sorters{padding:16px 9px;width:100%;> span{text-align:center;width:100%;}.ant-table-column-sorter{display:none;}}.ant-page-header{padding-bottom:0;}"], Object.values(TaskState).map(function (state) {
  return css(["&.state-", " .anticon{color:", ";}"], state, TaskStateColorDark[state]);
}), Colors.grey[0]);
var SearchInput = styled(Input).withConfig({
  displayName: "list__SearchInput",
  componentId: "sc-kulv8x-1"
})(["max-width:200px;"]);